import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CalendarTimeline = ({ assignments }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const getAssignmentsForDay = (day) => {
    const dateStr = `${String(currentMonth?.getMonth() + 1)?.padStart(2, '0')}/${String(day)?.padStart(2, '0')}/${currentMonth?.getFullYear()}`;
    return assignments?.filter(a => a?.startDate === dateStr || a?.endDate === dateStr);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="Calendar" size={20} color="var(--color-primary)" strokeWidth={2} />
            <h3 className="text-lg font-semibold text-foreground">
              {monthNames?.[currentMonth?.getMonth()]} {currentMonth?.getFullYear()}
            </h3>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" iconName="ChevronLeft" onClick={previousMonth} />
            <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date())}>
              Today
            </Button>
            <Button variant="outline" size="sm" iconName="ChevronRight" onClick={nextMonth} />
          </div>
        </div>
      </div>
      <div className="p-4">
        <div className="grid grid-cols-7 gap-2 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map((day) => (
            <div key={day} className="text-center text-xs font-semibold text-muted-foreground py-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: startingDayOfWeek })?.map((_, index) => (
            <div key={`empty-${index}`} className="aspect-square"></div>
          ))}

          {Array.from({ length: daysInMonth })?.map((_, index) => {
            const day = index + 1;
            const dayAssignments = getAssignmentsForDay(day);
            const isToday = new Date()?.getDate() === day && 
                           new Date()?.getMonth() === currentMonth?.getMonth() && 
                           new Date()?.getFullYear() === currentMonth?.getFullYear();

            return (
              <div
                key={day}
                className={`aspect-square p-2 border rounded-lg transition-hover cursor-pointer ${
                  isToday 
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/30'
                }`}
              >
                <div className="flex flex-col h-full">
                  <span className={`text-sm font-medium ${isToday ? 'text-primary' : 'text-foreground'}`}>
                    {day}
                  </span>
                  {dayAssignments?.length > 0 && (
                    <div className="mt-1 flex-1 flex flex-col justify-end">
                      <div className="flex items-center space-x-1">
                        <div className="w-1.5 h-1.5 bg-success rounded-full"></div>
                        <span className="text-xs text-muted-foreground">{dayAssignments?.length}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-success rounded-full"></div>
              <span>Active Assignments</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-warning rounded-full"></div>
              <span>Pending Assignments</span>
            </div>
          </div>
          <span>{assignments?.length} total assignments this month</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarTimeline;